/**
 * Heatframe Product Card Filtering System - Hardcoded JSON Configuration
 * Uses JSON config file for dropdown options and filtering logic
 */
class HeatframeFilter {
  constructor() {
    console.log('DEBUG: HeatframeFilter constructor called');
    
    // Initialize properties but don't find elements yet
    this.dropdowns = {};
    this.cards = [];
    this.gridContainer = null;
    this.config = null;
    this.initializationAttempts = 0;
    this.maxInitializationAttempts = 10;
    this.initializationCompleted = false;
    
    // Task 137: Initialize centered overlay properties
    this.overlayContainer = null;
    this.currentCenteredCard = null;
    this.keyboardHandler = null;
    
    // Task 138: Initialize variant availability tracking for centered cards
    this.centeredCardAvailabilityInstances = new Map();
    
    // Start async initialization
    this.initializeAsync();
  }
  
  async initializeAsync() {
    console.log('DEBUG: Starting async initialization');
    
    // Load JSON configuration first
    const configLoaded = await this.loadConfiguration();
    if (!configLoaded) {
      console.error('DEBUG: Failed to load configuration, aborting');
      return;
    }
    console.log('DEBUG: JSON configuration loaded successfully');
    
    // Render dropdowns with proper container checking
    const renderingSuccess = this.renderDropdowns();
    if (!renderingSuccess) {
      console.error('DEBUG: Failed to render dropdowns, aborting initialization');
      return;
    }
    console.log('DEBUG: Dropdown rendering completed successfully');
    
    // Now wait for elements (including the newly rendered dropdowns)
    const success = await this.waitForElements();
    if (success) {
      console.log('DEBUG: All elements found, proceeding with initialization');
      this.init();
      this.initializationCompleted = true;
      console.log('DEBUG: Filter initialization SUCCESS - all steps completed!');
    } else {
      console.error('DEBUG: Failed to find all required elements after retries');
    }
  }
  
  /**
   * Load dropdown configuration from JSON file with caching
   */
  async loadConfiguration() {
    console.log('DEBUG: Loading dropdown configuration...');
    
    // Check if we have valid cached configuration
    if (cachedFilterConfig && configLoadTime) {
      const now = Date.now();
      const cacheAge = now - configLoadTime;
      
      if (cacheAge < CONFIG_CACHE_DURATION) {
        console.log(`DEBUG: Using cached configuration (age: ${Math.round(cacheAge / 1000)}s)`);
        this.config = cachedFilterConfig;
        return true;
      } else {
        console.log('DEBUG: Cache expired, fetching fresh configuration');
      }
    }
    
    try {
      // Task 117: Use proper Shopify asset URL with fallback for backwards compatibility
      const configPath = window.heatframeConfigUrl || `${window.location.origin}/assets/heatframe-dropdown-config.json`;
      console.log(`DEBUG: Loading configuration from: ${configPath}${window.heatframeConfigUrl ? ' (using Shopify asset URL)' : ' (using fallback path)'}`);  
      
      const response = await fetch(configPath);
      
      if (!response.ok) {
        throw new Error(`Failed to load configuration: HTTP ${response.status}`);
      }
      
      this.config = await response.json();
      
      // Cache the configuration
      cachedFilterConfig = this.config;
      configLoadTime = Date.now();
      
      console.log('DEBUG: Configuration loaded and cached successfully');
      console.log(`DEBUG: Found ${this.config.dropdowns?.length || 0} dropdown configurations`);
      
      return true;
    } catch (error) {
      console.error('DEBUG: Failed to load configuration:', error);
      return false;
    }
  }
  
  async waitForElements() {
    return new Promise((resolve) => {
      const checkElements = () => {
        this.initializationAttempts++;
        console.log(`DEBUG: Element check attempt #${this.initializationAttempts}`);
        
        // Try to find all required elements
        const foundElements = this.findRequiredElements();
        
        if (foundElements.success) {
          console.log('DEBUG: All elements found successfully');
          resolve(true);
          return;
        }
        
        if (this.initializationAttempts >= this.maxInitializationAttempts) {
          console.error('DEBUG: Max initialization attempts reached');
          resolve(false);
          return;
        }
        
        // Exponential backoff: 100ms, 200ms, 400ms, 800ms, etc.
        const delay = Math.min(100 * Math.pow(2, this.initializationAttempts - 1), 2000);
        console.log(`DEBUG: Retrying in ${delay}ms...`);
        
        setTimeout(() => {
          requestAnimationFrame(checkElements);
        }, delay);
      };
      
      // Start checking
      requestAnimationFrame(checkElements);
    });
  }
  
  findRequiredElements() {
    console.log('DEBUG: Looking for required elements...');
    
    // Build dropdowns object from configuration
    const dropdowns = {};
    if (this.config && this.config.dropdowns) {
      this.config.dropdowns.forEach(dropdown => {
        const element = document.getElementById(dropdown.id);
        if (element) {
          // Use the dropdown id without "-dropdown" suffix as key
          const key = dropdown.id.replace('-dropdown', '');
          dropdowns[key] = element;
        }
      });
    }
    
    // Find cards and grid container
    const cards = document.querySelectorAll('.heatframe-product-grid .config-card');
    const gridContainer = document.querySelector('.heatframe-product-grid');
    
    // Check if all required elements exist
    const dropdownsFound = Object.keys(dropdowns).length;
    const expectedDropdowns = this.config ? this.config.dropdowns.length : 7;
    const cardsFound = cards.length > 0;
    const gridFound = gridContainer !== null;
    
    console.log('DEBUG: Element search results:', {
      dropdowns: `${dropdownsFound}/${expectedDropdowns}`,
      cards: cards.length,
      gridContainer: gridFound ? 'found' : 'not found'
    });
    
    // We need at least the grid container and most dropdowns
    const success = gridFound && dropdownsFound >= Math.max(4, expectedDropdowns - 2);
    
    if (success) {
      this.dropdowns = dropdowns;
      this.cards = cards;
      this.gridContainer = gridContainer;
      console.log('DEBUG: Successfully stored all elements');
    }
    
    return { success, dropdowns, cards, gridContainer };
  }
  
  init() {
    console.log('DEBUG: Starting filter initialization');
    
    // Add event listeners to all dropdowns
    let listenersAdded = 0;
    Object.values(this.dropdowns).forEach(dropdown => {
      if (dropdown) {
        dropdown.addEventListener('change', () => this.filterCards());
        listenersAdded++;
      }
    });
    
    // Initialize dropdown states on first load
    this.updateDropdownStates();
    
    console.log(`DEBUG: Added event listeners to ${listenersAdded} dropdowns`);
    
    // Add clear filters button
    this.addClearFiltersButton();

    // Set initial button visibility (should be hidden)
    this.updateClearButtonVisibility();

    // Task 137: Initialize centered overlay system
    this.initializeCenteredOverlay();
    
    // Success indicator
    console.log('DEBUG: HeatframeFilter initialization completed successfully!');
    console.log('DEBUG: Filter system is ready to use');
  }
  
  /**
   * Render dropdowns from JSON configuration
   * Now with reliable container checking
   */
  renderDropdowns() {
    console.log('DEBUG: Rendering dropdowns from configuration');
    
    // Ensure container exists before attempting to render
    const container = document.getElementById('dropdowns-container');
    if (!container) {
      console.error('DEBUG: Dropdowns container not found - cannot render dropdowns');
      return false;
    }
    
    if (!this.config || !this.config.dropdowns) {
      console.error('DEBUG: No dropdown configuration available');
      return false;
    }
    
    console.log('DEBUG: Container and configuration found, rendering dropdowns');
    
    // Clear existing content
    container.innerHTML = '';
    
    // Render each dropdown from configuration
    this.config.dropdowns.forEach(dropdownConfig => {
      const dropdownGroup = document.createElement('div');
      dropdownGroup.className = 'filter-dropdown-group';

      // Task 162: Apply width class if specified
      if (dropdownConfig.width) {
        dropdownGroup.classList.add(`filter-dropdown-${dropdownConfig.width}`);
      }

      // Create label with help icon (Task 118)
      const labelContainer = document.createElement('div');
      labelContainer.className = 'filter-label-container';
      
      const label = document.createElement('label');
      label.setAttribute('for', dropdownConfig.id);
      label.textContent = dropdownConfig.title;
      
      // Add help icon if helpImage or helpPdf is specified (backwards compatibility)
      const helpFile = dropdownConfig.helpImage || dropdownConfig.helpPdf;
      console.log(`DEBUG: Processing dropdown ${dropdownConfig.title}, helpFile:`, helpFile);
      if (helpFile) {
        const helpIcon = document.createElement('button');
        helpIcon.type = 'button';
        helpIcon.className = 'filter-help-icon';
        helpIcon.innerHTML = '?';
        helpIcon.setAttribute('aria-label', `Help for ${dropdownConfig.title}`);
        helpIcon.setAttribute('data-image', helpFile);
        helpIcon.setAttribute('data-filter-title', dropdownConfig.title);

        // Make label clickable with same data attributes
        label.setAttribute('data-image', helpFile);
        label.setAttribute('data-filter-title', dropdownConfig.title);
        label.setAttribute('role', 'button');
        label.setAttribute('tabindex', '0');
        
        labelContainer.appendChild(label);
        labelContainer.appendChild(helpIcon);
        console.log(`DEBUG: Added help icon for ${dropdownConfig.title}`);
      } else {
        labelContainer.appendChild(label);
      }
      
      // Create select element
      const select = document.createElement('select');
      select.id = dropdownConfig.id;
      select.className = 'filter-dropdown';
      
      // Add default option
      const defaultOption = document.createElement('option');
      defaultOption.value = '';
      defaultOption.textContent = dropdownConfig.defaultOption;
      select.appendChild(defaultOption);
      
      // Add configured options
      dropdownConfig.options.forEach(optionConfig => {
        const option = document.createElement('option');
        option.value = optionConfig.value;
        option.textContent = optionConfig.label;
        select.appendChild(option);
      });
      
      // Assemble the group
      dropdownGroup.appendChild(labelContainer);
      dropdownGroup.appendChild(select);
      container.appendChild(dropdownGroup);
    });
    
    console.log(`DEBUG: Successfully rendered ${this.config.dropdowns.length} dropdowns`);
    
    // Task 118: Setup help icon event listeners
    this.setupHelpIconListeners();
    
    return true;
  }
  
  /**
   * Task 118: Setup event listeners for help icons
   */
  setupHelpIconListeners() {
    console.log('DEBUG: Setting up help icon and label event listeners');
    
    // Setup help icon listeners
    const helpIcons = document.querySelectorAll('.filter-help-icon');
    helpIcons.forEach(icon => {
      icon.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        const imageFile = icon.getAttribute('data-image');
        const filterTitle = icon.getAttribute('data-filter-title');

        this.showPdfModal(imageFile, filterTitle);
      });
    });
    
    // Setup clickable label listeners
    const clickableLabels = document.querySelectorAll('.filter-label-container label[data-image]');
    clickableLabels.forEach(label => {
      // Click event
      label.addEventListener('click', (event) => {
        event.preventDefault();

        const imageFile = label.getAttribute('data-image');
        const filterTitle = label.getAttribute('data-filter-title');

        this.showPdfModal(imageFile, filterTitle);
      });

      // Keyboard accessibility (Enter and Space keys)
      label.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();

          const imageFile = label.getAttribute('data-image');
          const filterTitle = label.getAttribute('data-filter-title');

          this.showPdfModal(imageFile, filterTitle);
        }
      });
    });
    
    console.log(`DEBUG: Setup ${helpIcons.length} help icon listeners and ${clickableLabels.length} clickable label listeners`);
    console.log(`DEBUG: Help icons found:`, helpIcons);
    console.log(`DEBUG: Clickable labels found:`, clickableLabels);
  }
  
  /**
   * Show image modal with specified documentation image
   * Updated to display images instead of PDFs with responsive sizing
   */
  showPdfModal(imageFile, filterTitle) {
    console.log(`DEBUG: Showing image modal for ${imageFile}`);
    console.log(`DEBUG: Function called with:`, { imageFile, filterTitle });

    let imageUrl;

    // Check if imageFile is already a full URL (direct URL from config)
    if (imageFile && (imageFile.startsWith('http://') || imageFile.startsWith('https://') || imageFile.startsWith('/'))) {
      imageUrl = imageFile;
      console.log(`DEBUG: Using direct URL from config: ${imageUrl}`);
    } else {
      // Get the image URL from global variables (backwards compatibility with PDF URLs)
      imageUrl = (window.heatframeImageUrls && window.heatframeImageUrls[imageFile]) ||
                 (window.heatframePdfUrls && window.heatframePdfUrls[imageFile]);
      console.log(`DEBUG: Looking for URL for file: ${imageFile}`);
      console.log(`DEBUG: Found URL from window objects: ${imageUrl}`);
    }

    // If we still don't have a URL, show error
    if (!imageUrl) {
      console.error(`DEBUG: Image URL not found for ${imageFile}`);
      console.error(`DEBUG: Available image URLs:`, window.heatframeImageUrls);
      console.error(`DEBUG: Available PDF URLs:`, window.heatframePdfUrls);
      alert(`Help documentation for ${filterTitle} is not available.`);
      return;
    }

    // If it's a PDF URL, we'll display it as an image (might not render properly)
    if (imageUrl && imageUrl.includes('.pdf')) {
      console.warn(`DEBUG: Attempting to display PDF as image: ${imageUrl}`);
      console.warn(`DEBUG: This may not render properly. Consider updating the asset to an image file.`);
    }
    
    // Always append to body to prevent container overflow issues
    const heatframeContainer = document.body;
    
    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'heatframe-image-modal';
    modal.innerHTML = `
      <div class="heatframe-image-modal-content">
        <div class="heatframe-image-modal-header">
          <h3>${filterTitle} Specification</h3>
          <button class="heatframe-image-modal-close" aria-label="Close">&times;</button>
        </div>
        <div class="heatframe-image-modal-body">
          <div class="heatframe-image-container">
            <img
              src="${imageUrl}"
              alt="${filterTitle} Specification"
              class="heatframe-image-viewer">
          </div>
        </div>
      </div>
    `;
    
    // Add modal to page
    heatframeContainer.appendChild(modal);
    
    // Setup close functionality
    const closeBtn = modal.querySelector('.heatframe-image-modal-close');
    const modalContent = modal.querySelector('.heatframe-image-modal-content');
    
    const closeModal = () => {
      modal.remove();
    };
    
    // Close on X button
    closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      closeModal();
    });
    
    // Close on overlay click (clicking outside modal content)
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        e.preventDefault();
        e.stopPropagation();
        closeModal();
      }
    });
    
    // Close on ESC key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
    
    // Focus management for accessibility
    closeBtn.focus();
    
    console.log(`DEBUG: PDF modal created and shown for ${filterTitle}`);
  }
  
  /**
   * Main filtering function using AND logic
   */
  filterCards() {
    const filters = this.getActiveFilters();
    let visibleCount = 0;
    
    this.cards.forEach(card => {
      const matches = this.cardMatchesFilters(card, filters);
      
      if (matches) {
        card.parentElement.style.display = 'block';
        visibleCount++;
      } else {
        card.parentElement.style.display = 'none';
      }
    });
    
    // Handle empty results
    this.handleEmptyResults(visibleCount);
    
    // Update dropdown states based on remaining visible cards
    this.updateDropdownStates();

    // 🔧 FILTER INTEGRATION: Apply filters to any currently expanded cards
    this.applyFiltersToExpandedCards();

    // Update clear button visibility based on active filters
    this.updateClearButtonVisibility();
  }

  /**
   * Apply active filters to all currently expanded cards
   */
  applyFiltersToExpandedCards() {
    const activeFilters = this.getActiveFilters();
    
    if (Object.keys(activeFilters).length === 0) {
      return;
    }
    
    // Find all currently expanded heatframe cards
    const expandedCards = Array.from(this.cards).filter(card => {
      const configContainer = card.querySelector('.config-options-container');
      return configContainer && configContainer.classList.contains('open');
    });
    
    // Apply filters to each expanded card
    expandedCards.forEach(card => {
      this.applyFiltersToConfigOptions(card);
    });
  }
  
  /**
   * Get active filter values from all dropdowns
   * Updated to include empty string values (for filters like STANDARD PCB thickness or 160mm length)
   */
  getActiveFilters() {
    const filters = {};

    Object.entries(this.dropdowns).forEach(([key, dropdown]) => {
      if (dropdown && dropdown.value !== null && dropdown.value !== undefined) {
        // Check if an actual option is selected (not the default "Select" option)
        const selectedOption = dropdown.options[dropdown.selectedIndex];
        if (selectedOption && selectedOption.textContent.trim() !== 'Select') {
          // Include empty strings as valid filter values (e.g., STANDARD PCB thickness, 160mm length)
          filters[key] = dropdown.value;
        }
      }
    });

    return filters;
  }

  /**
   * Apply active filters to config-options when cards are expanded
   * This is the core integration function
   */
  applyFiltersToConfigOptions(card) {
    const activeFilters = this.getActiveFilters();
    
    if (Object.keys(activeFilters).length === 0) {
      return;
    }
    
    // Get the variant data for this card
    const variantData = this.getCardVariantData(card);
    if (!variantData) {
      return;
    }
    
    // Find config-options form within this card
    const configForm = card.querySelector('#custom-variant-form');
    if (!configForm) {
      return;
    }
    
    // Apply each active filter to corresponding config options
    Object.entries(activeFilters).forEach(([filterKey, filterValue]) => {
      this.applyFilterToOption(configForm, filterKey, filterValue, variantData);
    });
  }


  /**
   * Get variant data from a card
   */
  getCardVariantData(card) {
    try {
      const variantScript = card.querySelector('script[data-variants-json]');
      if (!variantScript) {
        return null;
      }
      
      const variantData = JSON.parse(variantScript.textContent);
      return variantData;
    } catch (error) {
      return null;
    }
  }

  /**
   * Apply a single filter to corresponding config option
   * Returns true if successful, false otherwise
   */
  applyFilterToOption(configForm, filterKey, filterValue, variantData) {
    // Get the dropdown config to understand the mapping
    const dropdownConfig = this.getDropdownConfig(filterKey);
    if (!dropdownConfig) {
      return false;
    }
    
    // Find the option that matches the filter value
    const matchingOption = dropdownConfig.options.find(opt => opt.value === filterValue);
    if (!matchingOption) {
      return false;
    }
    
    // Map filter key to option name in the product
    const optionMapping = this.getOptionMapping(filterKey);
    if (!optionMapping) {
      return false;
    }
    
    // Try multiple matching strategies for robustness
    const optionValue = this.getMatchingOptionValue(matchingOption, filterKey, variantData);
    if (!optionValue) {
      return false;
    }
    
    // Find and select the appropriate option in the config form
    const success = this.selectConfigOption(configForm, optionMapping, optionValue, variantData);
    return success !== false;
  }

  /**
   * Get the matching option value for a config form
   * Handles different matching strategies for different filter types
   * Task 162: Updated to handle separate length and orientation filters
   */
  getMatchingOptionValue(matchingOption, filterKey, variantData) {
    const label = matchingOption.label;
    const value = matchingOption.value;

    // Special handling for different filter types
    switch (filterKey) {
      case 'mezzanine':
        // For mezzanine, check if we have variant data to find exact matches
        return this.findBestOptionMatch(label, filterKey, variantData) || label;

      case 'vita':
        // For VITA, check variant data first
        return this.findBestOptionMatch(label, filterKey, variantData) || label;

      case 'size':
        // For size, check variant data first
        return this.findBestOptionMatch(label, filterKey, variantData) || label;

      case 'length':
        // Task 162: For length, map to combined length+orientation value
        return this.mapLengthOrientationValue(filterKey, value, label, variantData);

      case 'orientation':
        // Task 162: For orientation, map to combined length+orientation value
        return this.mapLengthOrientationValue(filterKey, value, label, variantData);

      case 'wedgelock':
        // For wedgelock, check variant data first
        return this.findBestOptionMatch(label, filterKey, variantData) || label;

      case 'pcb-thickness':
        // For PCB thickness, check variant data first
        return this.findBestOptionMatch(label, filterKey, variantData) || label;

      default:
        // Default to using the label, but try to find best match first
        return this.findBestOptionMatch(label, filterKey, variantData) || label;
    }
  }

  /**
   * Find the best matching option value from actual variant data
   */
  findBestOptionMatch(targetValue, filterKey, variantData) {
    if (!variantData || variantData.length === 0) {
      return null;
    }

    // Collect all unique option values from variant data
    const allOptionValues = new Set();
    
    variantData.forEach(variant => {
      if (variant.options) {
        variant.options.forEach(opt => {
          allOptionValues.add(opt);
        });
      }
    });

    // Try to find the best match using our enhanced text matching
    for (const actualOption of allOptionValues) {
      if (this.isTextMatch(actualOption, targetValue)) {
        return actualOption;
      }
    }

    // If no match found, return null to use the default
    return null;
  }

  /**
   * Task 162: Map length and orientation filter values to product option values
   * Combines length + orientation into the appropriate product option format
   */
  mapLengthOrientationValue(filterKey, filterValue, filterLabel, variantData) {
    // Get both length and orientation filter values
    const activeFilters = this.getActiveFilters();
    const lengthValue = filterKey === 'length' ? filterValue : (activeFilters.length || '');
    const orientationValue = filterKey === 'orientation' ? filterValue : (activeFilters.orientation || '');

    // Combine length and orientation to get the combined value
    const combinedValue = this.getCombinedLengthOrientation(lengthValue, orientationValue);

    // If we don't have both filters or invalid combination, just use the label
    if (!combinedValue) {
      return filterLabel;
    }

    // Mappings from combined value to likely product option names
    const orientationMappings = {
      'PS': ['PRIMARY SIDE (160MM)', 'PRIMARY SIDE', 'Primary Side', 'PS', 'PRIMARY', 'primary side'],
      'SS': ['SECONDARY SIDE (160MM)', 'SECONDARY SIDE', 'Secondary Side', 'SS', 'SECONDARY', 'secondary side'],
      'PT': ['PASS THROUGH (160MM)', 'PASS THROUGH', 'Pass Through', 'PT', 'PASSTHROUGH', 'pass through'],
      '100PS': ['PRIMARY SIDE (100MM)', 'PRIMARY SIDE 100MM', 'Primary Side 100mm', '100PS', 'PRIMARY 100', 'primary side 100'],
      '100SS': ['SECONDARY SIDE (100MM)', 'SECONDARY SIDE 100MM', 'Secondary Side 100mm', '100SS', 'SECONDARY 100', 'secondary side 100']
    };

    const possibleValues = orientationMappings[combinedValue] || [filterLabel];

    // If we have variant data, try to find the best match
    if (variantData && variantData.length > 0) {
      // Get all unique option values across all variants for orientation-like options
      const allOrientationOptions = new Set();

      variantData.forEach(variant => {
        if (variant.options) {
          variant.options.forEach(opt => {
            const optLower = opt.toLowerCase();
            if (optLower.includes('primary') || optLower.includes('secondary') ||
                optLower.includes('pass') || optLower.includes('through') ||
                optLower.includes('side') || /\d+mm/i.test(opt)) {
              allOrientationOptions.add(opt);
            }
          });
        }
      });

      // Find the best match using our enhanced text matching
      for (const actualOption of allOrientationOptions) {
        for (const possibleValue of possibleValues) {
          if (this.isTextMatch(actualOption, possibleValue)) {
            return actualOption; // Return the actual option from the product
          }
        }
      }
    }

    // Default to the most descriptive possible value
    return possibleValues[0];
  }

  /**
   * Map filter keys to product option names
   * Task 162: Added mapping for length filter
   */
  getOptionMapping(filterKey) {
    const mappings = {
      'vita': 'VITA',
      'size': 'Size',
      'length': 'Orientation', // Task 162: Length maps to Orientation option (combined with orientation)
      'orientation': 'Orientation',
      'wedgelock': 'Wedgelock Width',
      'mezzanine': 'Mezzanine',
      'pcb-thickness': 'PCB Thickness'
    };

    return mappings[filterKey];
  }

  /**
   * Select a config option in the form
   * Returns true if successful, false otherwise
   */
  selectConfigOption(configForm, optionName, optionValue, variantData) {
    // First, try to find pill/button selector
    const pillFieldset = this.findPillFieldset(configForm, optionName);
    
    if (pillFieldset) {
      return this.selectPillOption(pillFieldset, optionValue);
    }
    
    // Fallback to dropdown selector
    const selectElement = this.findDropdownSelect(configForm, optionName);
    
    if (selectElement) {
      return this.selectDropdownOption(selectElement, optionValue);
    }
    
    return false;
  }

  /**
   * Find pill fieldset with flexible matching
   */
  findPillFieldset(configForm, optionName) {
    const fieldsets = configForm.querySelectorAll('fieldset.config-pill');
    
    for (let fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend');
      if (!legend) continue;
      
      const legendText = legend.textContent.trim();
      
      // Exact match
      if (legendText === optionName) return fieldset;
      
      // Case-insensitive match
      if (legendText.toLowerCase() === optionName.toLowerCase()) return fieldset;
      
      // Partial match for orientation (common case)
      if (optionName.toLowerCase().includes('orientation') && 
          legendText.toLowerCase().includes('orientation')) return fieldset;
      
      // Partial match for wedgelock (common case)
      if (optionName.toLowerCase().includes('wedgelock') && 
          legendText.toLowerCase().includes('wedgelock')) return fieldset;
    }
    
    return null;
  }

  /**
   * Find dropdown select with flexible matching
   */
  findDropdownSelect(configForm, optionName) {
    const labels = configForm.querySelectorAll('label.form__label');
    
    for (let label of labels) {
      const labelText = label.textContent.trim();
      
      // Exact match
      if (labelText === optionName) {
        const selectElement = configForm.querySelector(`select#${label.getAttribute('for')}`);
        if (selectElement) return selectElement;
      }
      
      // Case-insensitive match
      if (labelText.toLowerCase() === optionName.toLowerCase()) {
        const selectElement = configForm.querySelector(`select#${label.getAttribute('for')}`);
        if (selectElement) return selectElement;
      }
      
      // Partial match for common cases
      if (optionName.toLowerCase().includes('orientation') && 
          labelText.toLowerCase().includes('orientation')) {
        const selectElement = configForm.querySelector(`select#${label.getAttribute('for')}`);
        if (selectElement) return selectElement;
      }
      
      if (optionName.toLowerCase().includes('wedgelock') && 
          labelText.toLowerCase().includes('wedgelock')) {
        const selectElement = configForm.querySelector(`select#${label.getAttribute('for')}`);
        if (selectElement) return selectElement;
      }
    }
    
    return null;
  }

  /**
   * Select an option in a pill/button selector
   * Returns true if successful, false otherwise
   */
  selectPillOption(fieldset, optionValue) {
    const inputs = fieldset.querySelectorAll('input[type="radio"]');
    
    // Try exact match first
    for (let input of inputs) {
      if (input.value === optionValue && !input.disabled) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    
    // Try case-insensitive match
    for (let input of inputs) {
      if (input.value.toLowerCase() === optionValue.toLowerCase() && !input.disabled) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    
    // Try enhanced partial matching with better text similarity
    for (let input of inputs) {
      if (!input.disabled && this.isTextMatch(input.value, optionValue)) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    
    return false;
  }

  /**
   * Select an option in a dropdown selector
   * Returns true if successful, false otherwise
   */
  selectDropdownOption(selectElement, optionValue) {
    const options = selectElement.querySelectorAll('option');
    
    // Try exact match first
    for (let option of options) {
      if (option.value === optionValue && !option.disabled) {
        selectElement.value = optionValue;
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    
    // Try case-insensitive match
    for (let option of options) {
      if (option.value.toLowerCase() === optionValue.toLowerCase() && !option.disabled) {
        selectElement.value = option.value;
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    
    // Try enhanced partial matching with better text similarity
    for (let option of options) {
      if (!option.disabled && this.isTextMatch(option.value, optionValue)) {
        selectElement.value = option.value;
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    
    return false;
  }

  /**
   * Normalize values for better matching
   */
  normalizeValue(value) {
    return value.toLowerCase().replace(/[\s\-_()]/g, '');
  }

  /**
   * Enhanced text matching function for better partial matching
   * Handles cases like "PRIMARY SIDE (160MM)" matching "PRIMARY SIDE"
   */
  isTextMatch(optionText, filterText) {
    // Simple contains check (both directions)
    if (optionText.includes(filterText) || filterText.includes(optionText)) {
      return true;
    }
    
    // Case-insensitive contains check
    const optionLower = optionText.toLowerCase();
    const filterLower = filterText.toLowerCase();
    if (optionLower.includes(filterLower) || filterLower.includes(optionLower)) {
      return true;
    }
    
    // Normalized comparison (removes spaces, punctuation)
    if (this.normalizeValue(optionText) === this.normalizeValue(filterText)) {
      return true;
    }
    
    // Word-based matching - check if key words match
    const optionWords = this.extractKeyWords(optionText);
    const filterWords = this.extractKeyWords(filterText);
    
    // Check if all filter words are found in option words
    const filterWordsFound = filterWords.every(filterWord => 
      optionWords.some(optionWord => 
        optionWord.includes(filterWord) || filterWord.includes(optionWord)
      )
    );
    
    if (filterWordsFound && filterWords.length > 0) {
      return true;
    }
    
    // Check if option words are found in filter words (reverse)
    const optionWordsFound = optionWords.every(optionWord => 
      filterWords.some(filterWord => 
        optionWord.includes(filterWord) || filterWord.includes(optionWord)
      )
    );
    
    if (optionWordsFound && optionWords.length > 0 && optionWords.length <= filterWords.length) {
      return true;
    }
    
    return false;
  }

  /**
   * Extract key words from text for matching
   * Filters out common words and focuses on meaningful terms
   */
  extractKeyWords(text) {
    const commonWords = ['the', 'and', 'or', 'of', 'to', 'a', 'an', 'is', 'are', 'was', 'were', 'mm'];
    
    return text.toLowerCase()
      .replace(/[()]/g, ' ') // Replace parentheses with spaces
      .split(/[\s\-_]+/)     // Split on spaces, hyphens, underscores
      .filter(word => word.length > 1) // Remove single characters
      .filter(word => !commonWords.includes(word)) // Remove common words
      .filter(word => word.trim() !== ''); // Remove empty strings
  }
  
  /**
   * Check if a card matches all active filters (AND logic)
   * New implementation: Check if any SKU contains the filter value as substring
   * Special case: VITA 78.0 + wedgelock width uses variant titles instead of SKU patterns
   */
  cardMatchesFilters(card, filters) {
    // If no filters are active, show all cards
    if (Object.keys(filters).length === 0) {
      return true;
    }
    
    // Check each filter against the card's SKUs
    return Object.entries(filters).every(([filterKey, filterValue]) => {
      return this.cardMatchesFilter(card, filterKey, filterValue);
    });
  }
  
  /**
   * Check if a card matches a single filter
   * Handles special case for VITA 78.0 + wedgelock width
   * Task 162: Handles combined length + orientation logic
   */
  cardMatchesFilter(card, filterKey, filterValue) {
    // Get all SKUs for this card (from data-skus attribute)
    const cardSkus = card.getAttribute('data-skus');

    // If card doesn't have SKUs, it doesn't match
    if (!cardSkus) {
      return false;
    }

    // Special case: VITA 78.0 + wedgelock width
    if (filterKey === 'wedgelock' && this.cardHasVita78(cardSkus)) {
      console.log('DEBUG: Applying VITA 78.0 wedgelock special case');
      return this.checkVita78WedgelockMatch(card, filterValue);
    }

    // Task 162: Special handling for length and orientation filters
    // These need to be combined before matching
    if (filterKey === 'length' || filterKey === 'orientation') {
      return this.cardMatchesLengthOrientationFilter(card, filterKey, filterValue);
    }

    // Standard SKU-based filtering - pass filterKey for context
    const skus = cardSkus.split(',');
    return skus.some(sku => {
      return this.skuMatchesValue(sku, filterValue, filterKey);
    });
  }
  
  /**
   * Task 162: Check if a card matches length and/or orientation filters
   * Handles combined logic for length + orientation
   */
  cardMatchesLengthOrientationFilter(card, currentFilterKey, currentFilterValue) {
    const cardSkus = card.getAttribute('data-skus');
    if (!cardSkus) {
      return false;
    }

    // Get current state of both length and orientation filters
    const activeFilters = this.getActiveFilters();

    // Check if filters are set (use 'in' operator to detect even empty string values)
    const hasLengthFilter = 'length' in activeFilters;
    const hasOrientationFilter = 'orientation' in activeFilters;

    // Get values with proper defaults
    const lengthValue = hasLengthFilter ? activeFilters.length : null;
    const orientationValue = hasOrientationFilter ? activeFilters.orientation : null;

    // If this is the filter being checked, use the passed value
    // Otherwise use the active filter value
    const effectiveLengthValue = currentFilterKey === 'length' ? currentFilterValue : lengthValue;
    const effectiveOrientationValue = currentFilterKey === 'orientation' ? currentFilterValue : orientationValue;

    // Check if we have active filters
    const hasEffectiveLengthFilter = effectiveLengthValue !== null && effectiveLengthValue !== undefined;
    const hasEffectiveOrientationFilter = effectiveOrientationValue !== null && effectiveOrientationValue !== undefined;

    const skus = cardSkus.split(',');

    // Case 1: Both length and orientation are selected
    if (hasEffectiveLengthFilter && hasEffectiveOrientationFilter) {
      const combinedValue = this.getCombinedLengthOrientation(effectiveLengthValue, effectiveOrientationValue);
      if (!combinedValue) {
        return false; // Invalid combination (e.g., 100mm + Pass Through)
      }
      return skus.some(sku => this.skuMatchesValue(sku, combinedValue));
    }

    // Case 2: Only length is selected
    if (hasEffectiveLengthFilter && !hasEffectiveOrientationFilter) {
      if (effectiveLengthValue === '') {
        // 160mm (Standard) - match PS, SS, or PT patterns BUT NOT 100PS or 100SS
        // Example SKU: OC-48.2-6U-PS-250-N (160mm)
        // Should NOT match: OC-48.2-6U-100PS-250-N (100mm)
        return skus.some(sku => {
          // Exclude 100mm variants first
          if (sku.includes('-100PS-') || sku.includes('-100SS-')) {
            return false;
          }
          // Match 160mm variants (orientation code preceded by hyphen, not by 100)
          return sku.includes('-PS-') || sku.includes('-SS-') || sku.includes('-PT-');
        });
      } else if (effectiveLengthValue === '100') {
        // 100mm - match 100PS or 100SS patterns only
        // Example SKU: OC-48.2-6U-100PS-250-N
        return skus.some(sku =>
          sku.includes('-100PS-') || sku.includes('-100SS-')
        );
      }
    }

    // Case 3: Only orientation is selected
    if (!hasEffectiveLengthFilter && hasEffectiveOrientationFilter) {
      if (effectiveOrientationValue === 'PT') {
        // Pass Through only exists for 160mm
        return skus.some(sku => this.skuMatchesValue(sku, 'PT'));
      } else {
        // PS or SS - match both standard and 100mm variants
        return skus.some(sku =>
          this.skuMatchesValue(sku, effectiveOrientationValue) ||
          this.skuMatchesValue(sku, '100' + effectiveOrientationValue)
        );
      }
    }

    // Case 4: Neither filter is selected (shouldn't happen in normal flow)
    return true;
  }

  /**
   * Task 162: Combine length and orientation values into a single filter value
   * Returns null for invalid combinations
   */
  getCombinedLengthOrientation(lengthValue, orientationValue) {
    // Pass Through (PT) is not available for 100mm frames
    if (lengthValue === '100' && orientationValue === 'PT') {
      return null; // Invalid combination
    }

    if (lengthValue === '' || !lengthValue) {
      // Standard 160mm
      return orientationValue; // Returns: PS, SS, or PT
    } else if (lengthValue === '100') {
      // 100mm frames
      return lengthValue + orientationValue; // Returns: 100PS or 100SS
    }

    return null;
  }

  /**
   * Check if card has VITA 78.0 products
   */
  cardHasVita78(cardSkus) {
    const skus = cardSkus.split(',');
    return skus.some(sku => sku.includes('78'));
  }
  
  /**
   * Check VITA 78.0 wedgelock match using variant titles
   */
  checkVita78WedgelockMatch(card, wedgelockValue) {
    // Get variant titles
    const variantTitles = card.getAttribute('data-variant-titles');
    if (!variantTitles) {
      console.log('DEBUG: No variant titles found for VITA 78.0 wedgelock check');
      return false;
    }
    
    // Get the wedgelock label from config (e.g., "375" -> ".375")
    const wedgelockConfig = this.getDropdownConfig('wedgelock');
    if (!wedgelockConfig) {
      return false;
    }
    
    const wedgelockOption = wedgelockConfig.options.find(opt => opt.value === wedgelockValue);
    if (!wedgelockOption) {
      return false;
    }
    
    const wedgelockLabel = wedgelockOption.label; // e.g., ".375"
    console.log(`DEBUG: Looking for wedgelock label "${wedgelockLabel}" in variant titles`);
    
    // Split variant titles and check if any contains the wedgelock label
    const titles = variantTitles.split('|');
    const hasMatch = titles.some(title => title.includes(wedgelockLabel));
    
    console.log(`DEBUG: VITA 78.0 wedgelock match result: ${hasMatch}`);
    return hasMatch;
  }
  
  /**
   * Get dropdown configuration by key
   */
  getDropdownConfig(filterKey) {
    if (!this.config || !this.config.dropdowns) {
      return null;
    }
    
    return this.config.dropdowns.find(dropdown => {
      const key = dropdown.id.replace('-dropdown', '');
      return key === filterKey;
    });
  }
  
  /**
   * Handle empty filter results
   */
  handleEmptyResults(visibleCount) {
    // Remove existing empty message
    const existingMessage = this.gridContainer.querySelector('.empty-results-message');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Add empty message if no cards are visible
    if (visibleCount === 0) {
      const message = document.createElement('div');
      message.className = 'empty-results-message';
      message.style.cssText = `
        text-align: center;
        padding: 2rem;
        font-size: 1.2rem;
        color: #666;
        grid-column: 1 / -1;
        margin: 1rem 0;
      `;
      message.textContent = 'No products match your current filters. Try adjusting your selections.';
      this.gridContainer.appendChild(message);
    }
  }
  
  /**
   * Add clear filters button to the filter menu
   */
  addClearFiltersButton() {
    const filterMenu = document.querySelector('.heatframe-filter-menu');
    if (!filterMenu) {
      console.warn('DEBUG: Could not find filter menu for clear button');
      return;
    }
    
    // Check if button already exists
    const existingButton = filterMenu.querySelector('.clear-filters-button');
    if (existingButton) {
      console.log('DEBUG: Clear filters button already exists');
      return;
    }
    
    console.log('DEBUG: Adding clear filters button');
    
    const clearButton = document.createElement('button');
    clearButton.className = 'clear-filters-button hidden';
    clearButton.textContent = '×';
    
    clearButton.addEventListener('mouseover', () => {
      clearButton.style.backgroundColor = '#dc2626';
      clearButton.style.color = 'white';
    });
    
    clearButton.addEventListener('mouseout', () => {
      clearButton.style.backgroundColor = '#fee2e2';
      clearButton.style.color = '#dc2626';
    });
    
    clearButton.addEventListener('click', () => this.clearAllFilters());
    
    filterMenu.appendChild(clearButton);
    console.log('DEBUG: Clear filters button added successfully');
  }

  /**
   * Update clear filters button visibility based on active filters
   */
  updateClearButtonVisibility() {
    const clearButton = document.querySelector('.clear-filters-button');

    if (!clearButton) {
      console.log('DEBUG: Clear button not found for visibility update');
      return;
    }

    const activeFilters = this.getActiveFilters();
    const hasActiveFilters = Object.keys(activeFilters).length > 0;

    if (hasActiveFilters) {
      clearButton.classList.remove('hidden');
      clearButton.classList.add('visible');
      console.log('DEBUG: Clear button shown - active filters:', Object.keys(activeFilters).length);
    } else {
      clearButton.classList.remove('visible');
      clearButton.classList.add('hidden');
      console.log('DEBUG: Clear button hidden - no active filters');
    }
  }

  /**
   * Analyze available variants based on currently visible cards
   * Returns object mapping dropdown keys to arrays of available values
   * Task 162: Updated to handle separate length and orientation extraction
   */
  analyzeAvailableVariants() {
    const availableOptions = {};

    // Initialize available options object for each dropdown
    if (this.config && this.config.dropdowns) {
      this.config.dropdowns.forEach(dropdown => {
        const key = dropdown.id.replace('-dropdown', '');
        availableOptions[key] = new Set();
      });
    }

    // Get currently visible cards
    const visibleCards = Array.from(this.cards).filter(card => {
      return card.parentElement.style.display !== 'none';
    });

    console.log(`DEBUG: Analyzing variants from ${visibleCards.length} visible cards`);

    // Analyze SKUs from visible cards
    visibleCards.forEach(card => {
      const cardSkus = card.getAttribute('data-skus');
      if (!cardSkus) return;

      const skus = cardSkus.split(',');

      // For each SKU, extract values for each dropdown category
      skus.forEach(sku => {
        this.config.dropdowns.forEach(dropdown => {
          const key = dropdown.id.replace('-dropdown', '');

          // Task 162: Special handling for length and orientation filters
          if (key === 'length') {
            // Extract length from SKU patterns
            if (sku.includes('-100PS-') || sku.includes('-100SS-')) {
              availableOptions[key].add('100');
            } else if (sku.includes('-PS-') || sku.includes('-SS-') || sku.includes('-PT-')) {
              availableOptions[key].add(''); // 160mm (Standard)
            }
          } else if (key === 'orientation') {
            // Extract orientation from SKU patterns
            if (sku.includes('-100PS-')) {
              availableOptions[key].add('PS');
            } else if (sku.includes('-100SS-')) {
              availableOptions[key].add('SS');
            } else if (sku.includes('-PS-')) {
              availableOptions[key].add('PS');
            } else if (sku.includes('-SS-')) {
              availableOptions[key].add('SS');
            } else if (sku.includes('-PT-')) {
              availableOptions[key].add('PT');
            }
          } else if (key === 'wedgelock' && sku.includes('78')) {
            // Special case: VITA 78.0 + wedgelock width
            const variantTitles = card.getAttribute('data-variant-titles');
            if (variantTitles) {
              const titles = variantTitles.split('|');
              dropdown.options.forEach(option => {
                const wedgelockLabel = option.label; // e.g., ".375"
                if (titles.some(title => title.includes(wedgelockLabel))) {
                  availableOptions[key].add(option.value);
                }
              });
            }
          } else {
            // Standard SKU-based analysis - pass filterKey for context
            dropdown.options.forEach(option => {
              if (this.skuMatchesValue(sku, option.value, key)) {
                availableOptions[key].add(option.value);
              }
            });
          }
        });
      });
    });

    // Convert Sets to Arrays for easier handling
    Object.keys(availableOptions).forEach(key => {
      availableOptions[key] = Array.from(availableOptions[key]);
    });

    console.log('DEBUG: Available variants analysis:', availableOptions);
    return availableOptions;
  }
  
  /**
   * Check if a SKU matches a dropdown value using the same logic as cardMatchesFilters
   * Task 162: Updated to handle combined length+orientation values (e.g., '100PS', '100SS')
   * Updated to handle empty values with filter context
   */
  skuMatchesValue(sku, value, filterKey) {
    // Handle empty value case (for "Standard" PCB thickness or 160mm length)
    if (value === '') {
      // Need to know which filter this is for to properly exclude the opposite
      if (filterKey === 'pcb-thickness') {
        // For STANDARD PCB thickness, exclude SKUs ending with -T (thick)
        // Example: OC-48.2-6U-PS-250-N (standard) vs OC-48.2-6U-PS-250-N-T (thick)
        return !sku.endsWith('-T');
      }
      // For other empty values (like 160mm length), this is handled elsewhere
      return true;
    }

    // Check if SKU contains the filter value with hyphen separators
    return sku.includes(`-${value}-`) || sku.includes(`-${value}`) || sku.endsWith(`-${value}`);
  }
  
  /**
   * Update dropdown states based on available variants
   * Task 162: Added logic to disable Pass Through when 100mm is selected
   */
  updateDropdownStates() {
    const availableVariants = this.analyzeAvailableVariants();
    const activeFilters = this.getActiveFilters();

    console.log('DEBUG: Updating dropdown states based on available variants');

    // Update each dropdown
    Object.entries(this.dropdowns).forEach(([key, dropdown]) => {
      if (!dropdown) return;

      const availableValues = availableVariants[key] || [];
      const isCurrentlySelected = activeFilters.hasOwnProperty(key);

      // Get all option elements in this dropdown
      const options = dropdown.querySelectorAll('option');

      options.forEach(option => {
        const value = option.value;
        const optionText = option.textContent.trim();

        // Task 212: Always keep the default "Select" option enabled
        // Check textContent because value is empty string for both "Select" and some valid filters
        if (optionText === 'Select') {
          option.disabled = false;
          option.classList.remove('unavailable');
          return;
        }

        // Task 162: Special handling for orientation dropdown when length is 100mm
        if (key === 'orientation' && activeFilters.length === '100') {
          // Disable Pass Through for 100mm frames
          if (value === 'PT') {
            option.disabled = true;
            option.classList.add('unavailable');

            // If Pass Through is currently selected, clear it
            if (dropdown.value === 'PT') {
              dropdown.value = '';
              // Trigger change event to re-filter
              dropdown.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
          }
        }

        // If this dropdown has an active selection, don't disable its selected option
        if (isCurrentlySelected && value === activeFilters[key]) {
          option.disabled = false;
          option.classList.remove('unavailable');
          return;
        }

        // Enable/disable based on availability
        const isAvailable = availableValues.includes(value);
        option.disabled = !isAvailable;

        if (isAvailable) {
          option.classList.remove('unavailable');
        } else {
          option.classList.add('unavailable');
        }
      });
    });
  }
  
  /**
   * Clear all dropdown selections
   */
  clearAllFilters() {
    if (!this.config || !this.config.dropdowns) {
      console.error('DEBUG: No configuration available for clearing filters');
      return;
    }
    
    // Reset all dropdowns to their default values
    this.config.dropdowns.forEach(dropdownConfig => {
      const dropdown = document.getElementById(dropdownConfig.id);
      if (dropdown && !dropdown.disabled) {
        dropdown.value = '';
      }
    });
    
    console.log('DEBUG: All filters cleared');

    // Re-filter to show all cards and reset dropdown states
    this.filterCards();

    // Update button visibility (will hide it since filters are cleared)
    this.updateClearButtonVisibility();
  }

  /**
   * Task 137: Initialize centered overlay system for heatframe cards
   */
  initializeCenteredOverlay() {
    console.log('DEBUG: Task 137 - Initializing centered overlay system');
    
    // Create overlay container if it doesn't exist
    this.createOverlayContainer();
    
    // Add click listeners to heatframe cards
    this.addCardClickListeners();
    
    // Add keyboard listener for ESC key
    this.addKeyboardListeners();
    
    console.log('DEBUG: Task 137 - Centered overlay system initialized');
  }

  /**
   * Task 137: Create the overlay container
   */
  createOverlayContainer() {
    // Find the heatframe container - be more specific about the structure
    let heatframeContainer = document.querySelector('.heatframe-single-container');
    
    if (!heatframeContainer) {
      // Try alternative selectors within the dynamically loaded content
      heatframeContainer = document.querySelector('#heatframe-selector .heatframe-single-container') ||
                          document.querySelector('#secondSidebar .heatframe-single-container') ||
                          document.querySelector('.side-bar-item .heatframe-single-container');
    }
    
    if (!heatframeContainer) {
      console.error('DEBUG: Task 137 - Could not find heatframe container in dynamic content');
      console.log('DEBUG: Task 137 - Available containers:', document.querySelectorAll('div[class*="heatframe"]'));
      return;
    }

    console.log('DEBUG: Task 137 - Found heatframe container:', heatframeContainer);

    // Check if overlay already exists
    let overlay = heatframeContainer.querySelector('.heatframe-centered-overlay');
    if (overlay) {
      console.log('DEBUG: Task 137 - Overlay container already exists');
      this.overlayContainer = overlay;
      return;
    }

    // Create overlay
    overlay = document.createElement('div');
    overlay.className = 'heatframe-centered-overlay hidden';
    overlay.setAttribute('data-overlay', 'heatframe-centered');
    
    // Ensure container has relative positioning for proper overlay placement
    const containerStyle = window.getComputedStyle(heatframeContainer);
    if (containerStyle.position === 'static') {
      heatframeContainer.style.position = 'relative';
      console.log('DEBUG: Task 137 - Set container position to relative');
    }
    
    heatframeContainer.appendChild(overlay);
    this.overlayContainer = overlay;
    
    console.log('DEBUG: Task 137 - Overlay container created and attached');
  }

  /**
   * Task 137: Add click listeners to heatframe cards
   */
  addCardClickListeners() {
    // Search for cards dynamically in the current DOM context
    const currentCards = document.querySelectorAll('.heatframe-product-grid .config-card[data-config-card]');
    
    if (!currentCards || currentCards.length === 0) {
      console.warn('DEBUG: Task 137 - No heatframe cards found in DOM for click listeners');
      
      // Try alternative selectors for dynamically loaded content
      const alternativeCards = document.querySelectorAll('#secondSidebar .config-card[data-config-card], .heatframe-single-container .config-card[data-config-card]');
      
      if (alternativeCards.length === 0) {
        console.warn('DEBUG: Task 137 - No alternative cards found either');
        return;
      }
      
      console.log(`DEBUG: Task 137 - Found ${alternativeCards.length} cards using alternative selectors`);
      this.attachClickListeners(alternativeCards);
      return;
    }

    console.log(`DEBUG: Task 137 - Found ${currentCards.length} cards using standard selector`);
    this.attachClickListeners(currentCards);
  }

  /**
   * Task 137: Helper method to attach click listeners to card elements
   */
  attachClickListeners(cards) {
    let newListenersAdded = 0;
    Array.from(cards).forEach((card) => {
      // Task 141: Skip cards that already have listeners
      if (card.hasAttribute('data-has-heatframe-listeners')) {
        return; // Skip this card
      }
      
      newListenersAdded++;
      
      // Remove any existing centered overlay listeners to prevent duplicates
      card.removeEventListener('click', card._centeredOverlayHandler);
      
      // Create and store the handler for later removal
      card._centeredOverlayHandler = (event) => {
        // Don't trigger if clicking on links or form elements
        if (event.target.closest('a') || event.target.closest('button') || event.target.closest('input') || event.target.closest('select')) {
          return;
        }
        
        // Don't trigger if clicking on the existing expand/collapse functionality
        if (event.target.closest('.config-options-container') && !event.target.closest('.config-card')) {
          return;
        }
        
        event.preventDefault();
        event.stopPropagation();
        
        // NEW: Extract heatframe handle and trigger radio selection to sync form state
        const heatframeData = this.extractHeatframeData(card);
        if (heatframeData.handle) {
          // Find and trigger the corresponding radio button to sync form state
          const radioInput = document.querySelector(`input[name="heatframe-selector-radio"][value="${heatframeData.handle}"]`);
          if (radioInput && !radioInput.checked) {
            console.log('🔄 Task 140: Triggering radio selection to sync card expansion:', heatframeData.handle);
            radioInput.checked = true;
            radioInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          
          // Also set window variables as backup (though radio should handle tempHeatframe)
          window.tempHeatframe = heatframeData.handle;
          window.tempHeatframeSku = heatframeData.sku; // Store first/default SKU as backup
          
          console.log('🔥 Heatframe modal sync completed at:', new Date().toISOString(), {
            handle: heatframeData.handle,
            sku: heatframeData.sku,
            allSkus: heatframeData.allSkus,
            radioTriggered: radioInput ? 'yes' : 'no'
          });
        }
        
        this.showCenteredCard(card);
      };
      
      // Add the click listener
      card.addEventListener('click', card._centeredOverlayHandler);
      
      // Task 141: Mark card as having listeners for duplicate prevention
      card.setAttribute('data-has-heatframe-listeners', 'true');
    });
    
    console.log(`✅ Task 141: Added click listeners to ${newListenersAdded}/${cards.length} cards (${cards.length - newListenersAdded} already had listeners)`);
  }

  /**
   * Task 139 + 140: Extract heatframe handle and SKU data from config card
   */
  extractHeatframeData(card) {
    try {
      let handle = null;
      let allSkus = [];
      
      // First, extract SKU data from the card's data attributes
      const skusAttr = card.getAttribute('data-skus');
      console.log('🔍 DEBUG: Card data-skus attribute:', skusAttr);
      console.log('🔍 DEBUG: Card element:', card);
      console.log('🔍 DEBUG: All card attributes:', Array.from(card.attributes).map(attr => `${attr.name}="${attr.value}"`));
      
      if (skusAttr && skusAttr.trim() !== '') {
        allSkus = skusAttr.split(',').map(sku => sku.trim()).filter(sku => sku !== '');
        console.log('🔥 SKUs found in card data:', allSkus);
      } else {
        console.warn('⚠️ No data-skus attribute found or it is empty');
      }
      
      // Method 1: Try to get handle from associated radio input
      const label = card.closest('label');
      if (label) {
        const radioInput = label.querySelector('input[type="radio"][name="heatframe-selector-radio"]');
        if (radioInput && radioInput.value) {
          handle = radioInput.value;
          console.log('🔥 Handle extracted from radio input:', handle);
        }
      }
      
      // Method 2: Try to extract from product URL in the card (fallback)
      if (!handle) {
        const productLink = card.querySelector('a[href*="/products/"]');
        if (productLink) {
          const href = productLink.getAttribute('href');
          const handleMatch = href.match(/\/products\/([^/?]+)/);
          if (handleMatch && handleMatch[1]) {
            handle = handleMatch[1];
            console.log('🔥 Handle extracted from product URL:', handle);
          }
        }
      }
      
      // Method 3: Try to find radio input by searching upward in DOM (fallback)
      if (!handle) {
        let currentElement = card.parentElement;
        while (currentElement && !currentElement.matches('body')) {
          const radioInput = currentElement.querySelector('input[type="radio"][name="heatframe-selector-radio"]');
          if (radioInput && radioInput.value) {
            handle = radioInput.value;
            console.log('🔥 Handle extracted from DOM traversal:', handle);
            break;
          }
          currentElement = currentElement.parentElement;
        }
      }
      
      // Return extracted data
      if (handle) {
        return {
          handle: handle,
          sku: allSkus.length > 0 ? allSkus[0] : null, // Use first SKU as default
          allSkus: allSkus
        };
      } else {
        console.warn('❌ Could not extract heatframe handle from card:', card);
        return { handle: null, sku: null, allSkus: [] };
      }
      
    } catch (error) {
      console.error('❌ Error extracting heatframe data:', error);
      return { handle: null, sku: null, allSkus: [] };
    }
  }

  /**
   * Task 137: Add keyboard listeners for ESC key
   */
  addKeyboardListeners() {
    this.keyboardHandler = (event) => {
      if (event.key === 'Escape') {
        this.hideCenteredCard();
      }
    };
    
    document.addEventListener('keydown', this.keyboardHandler);
    console.log('DEBUG: Task 137 - Keyboard listeners added');
  }

  /**
   * Task 137: Show card in centered overlay
   */
  showCenteredCard(originalCard) {
    console.log('DEBUG: Task 137 - Showing centered card');
    
    // Close any existing centered card first (mutual exclusion)
    this.hideCenteredCard();
    
    if (!this.overlayContainer) {
      console.error('DEBUG: Task 137 - No overlay container available');
      return;
    }

    // Clone the card
    const clonedCard = originalCard.cloneNode(true);
    clonedCard.className = clonedCard.className + ' heatframe-centered-card';
    
    // Ensure proper form functionality in cloned card
    this.setupClonedCardFunctionality(clonedCard, originalCard);
    
    // Add close button
    const closeButton = document.createElement('button');
    closeButton.className = 'heatframe-overlay-close';
    closeButton.innerHTML = '×';
    closeButton.setAttribute('aria-label', 'Close');
    closeButton.addEventListener('click', () => this.hideCenteredCard());
    
    clonedCard.appendChild(closeButton);
    
    // Clear overlay and add cloned card
    this.overlayContainer.innerHTML = '';
    this.overlayContainer.appendChild(clonedCard);
    
    // Show overlay
    this.overlayContainer.classList.remove('hidden');
    
    // Add click outside to close
    this.overlayContainer.addEventListener('click', (event) => {
      if (event.target === this.overlayContainer) {
        this.hideCenteredCard();
      }
    });
    
    // Store reference to original card for cleanup
    this.currentCenteredCard = {
      original: originalCard,
      cloned: clonedCard
    };
    
    console.log('DEBUG: Task 137 - Centered card displayed');
  }

  /**
   * Task 137: Hide centered card overlay
   */
  hideCenteredCard() {
    if (!this.overlayContainer) {
      return;
    }

    console.log('DEBUG: Task 137 - Hiding centered card');
    
    // Task 138: Clean up variant availability instances before clearing content
    this.cleanupCenteredCardVariantAvailability();
    
    // Hide overlay
    this.overlayContainer.classList.add('hidden');
    
    // Clear contents
    this.overlayContainer.innerHTML = '';
    
    // Clean up reference
    this.currentCenteredCard = null;
    
    console.log('DEBUG: Task 137 - Centered card hidden');
  }

  /**
   * Task 137: Setup functionality for cloned card to preserve form interactions
   */
  setupClonedCardFunctionality(clonedCard, originalCard) {
    console.log('DEBUG: Task 137 - Setting up cloned card functionality');
    
    // Generate unique IDs for form elements to prevent conflicts
    const timestamp = Date.now();
    const randomId = Math.floor(Math.random() * 1000);
    const uniqueSuffix = `_cloned_${timestamp}_${randomId}`;
    
    // Update form element IDs and their references
    const formElements = clonedCard.querySelectorAll('input, select, textarea, label, form');
    formElements.forEach((element) => {
      // Update IDs
      if (element.id) {
        element.id = element.id + uniqueSuffix;
      }
      
      // Update label 'for' attributes
      if (element.tagName === 'LABEL' && element.getAttribute('for')) {
        element.setAttribute('for', element.getAttribute('for') + uniqueSuffix);
      }
      
      // Update form attribute references
      if (element.getAttribute('form')) {
        element.setAttribute('form', element.getAttribute('form') + uniqueSuffix);
      }
    });
    
    // Preserve any existing event listeners by re-executing relevant scripts
    // This is important for form validation, variant selection, etc.
    const scripts = clonedCard.querySelectorAll('script');
    scripts.forEach(script => {
      if (script.src) {
        // Skip external scripts to avoid re-loading
        return;
      }
      
      try {
        // Re-evaluate inline scripts in the context of the cloned card
        const scriptContent = script.textContent;
        if (scriptContent.trim()) {
          // Wrap script execution to avoid global conflicts
          const wrappedScript = `
            (function() {
              const card = arguments[0];
              ${scriptContent}
            })(clonedCard);
          `;
          eval(wrappedScript);
        }
      } catch (error) {
        console.warn('DEBUG: Task 137 - Could not re-evaluate script in cloned card:', error);
      }
    });
    
    // Apply active filters to the cloned card if any are active
    this.applyFiltersToConfigOptions(clonedCard);
    
    // Task 138: Initialize variant availability system for cloned card
    this.initializeClonedCardVariantAvailability(clonedCard);

    // Task 195: Initialize lead time display for cloned card
    if (typeof window.initializeConfigCardLeadTime === 'function') {
      console.log('DEBUG: Task 195 - Initializing lead time for cloned card');
      // Small delay to ensure cloned card is fully rendered
      setTimeout(() => {
        window.initializeConfigCardLeadTime();
      }, 50);
    }

    console.log('DEBUG: Task 137 - Cloned card functionality setup complete');
  }

  /**
   * Task 138: Initialize variant availability system for cloned cards
   */
  initializeClonedCardVariantAvailability(clonedCard) {
    console.log('DEBUG: Task 138 - Initializing variant availability for cloned card');
    
    try {
      // Check if this card has variant JSON (heatframes only)
      const hasVariantJson = clonedCard.querySelector('[data-variants-json]');
      const hasButtonPicker = clonedCard.querySelector('fieldset.js.config-pill');
      
      if (!hasVariantJson || !hasButtonPicker) {
        console.log('DEBUG: Task 138 - Cloned card does not have variant data or button picker, skipping');
        return;
      }
      
      // Import and initialize ConfigCardVariantAvailability class
      if (typeof ConfigCardVariantAvailability !== 'undefined') {
        const availabilityInstance = new ConfigCardVariantAvailability(clonedCard);
        
        // Store reference to cleanup later
        if (!this.centeredCardAvailabilityInstances) {
          this.centeredCardAvailabilityInstances = new Map();
        }
        this.centeredCardAvailabilityInstances.set(clonedCard, availabilityInstance);
        
        console.log('DEBUG: Task 138 - Variant availability initialized for cloned card');
      } else {
        console.warn('DEBUG: Task 138 - ConfigCardVariantAvailability class not available');
      }
      
    } catch (error) {
      console.error('DEBUG: Task 138 - Error initializing variant availability for cloned card:', error);
    }
  }

  /**
   * Task 138: Clean up variant availability instances for centered cards
   */
  cleanupCenteredCardVariantAvailability() {
    if (!this.centeredCardAvailabilityInstances) {
      return;
    }
    
    console.log('DEBUG: Task 138 - Cleaning up variant availability instances');
    
    // Clear all stored instances
    this.centeredCardAvailabilityInstances.clear();
    
    console.log('DEBUG: Task 138 - Variant availability cleanup complete');
  }

  /**
   * Task 137: Check if a card is currently centered
   */
  isCardCentered() {
    return this.currentCenteredCard !== null;
  }

  /**
   * Task 137: Cleanup method for centered overlay system
   */
  cleanupCenteredOverlay() {
    console.log('DEBUG: Task 137 - Cleaning up centered overlay system');
    
    // Remove keyboard listeners
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
      this.keyboardHandler = null;
    }
    
    // Hide any open centered card
    this.hideCenteredCard();
    
    // Remove overlay container
    if (this.overlayContainer) {
      this.overlayContainer.remove();
      this.overlayContainer = null;
    }
    
    console.log('DEBUG: Task 137 - Centered overlay cleanup complete');
  }
}

// Global filter instance and configuration cache
let heatframeFilterInstance = null;
let cachedFilterConfig = null;
let configLoadTime = null;
const CONFIG_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

// Task 141: Add initialization lock to prevent concurrent initializations
let isInitializing = false;

// Simplified, reliable initialization function with optional background mode
async function initializeHeatframeFilter(backgroundPreload = false) {
  // Task 141: Prevent concurrent initialization calls
  if (isInitializing) {
    console.log('DEBUG: Task 141 - Initialization already in progress, skipping duplicate call');
    return;
  }
  
  console.log('DEBUG: Starting filter initialization', backgroundPreload ? '(background preload)' : '(interactive)');
  isInitializing = true;
  
  try {
    // Robust validation: Check both instance state AND actual DOM existence
  if (heatframeFilterInstance && heatframeFilterInstance.initializationCompleted) {
    const dropdownsContainer = document.getElementById('dropdowns-container');
    const hasRenderedDropdowns = dropdownsContainer && dropdownsContainer.children.length > 0;
    
    if (hasRenderedDropdowns) {
      console.log('✅ Task 141: Filter already initialized and DOM elements exist, skipping');
      return;
    } else {
      console.log('⚠️ Task 141: Filter instance exists but DOM elements missing - reinitializing');
      // Reset instance to allow reinitialization 
      heatframeFilterInstance.initializationCompleted = false;
    }
  }
  
  // Check that required container exists
  const dropdownsContainer = document.getElementById('dropdowns-container');
  const heatframeDrawer = document.getElementById('heatframe-selector');
  
  if (!dropdownsContainer || !heatframeDrawer) {
    console.log('DEBUG: Required DOM elements not found - container:', !!dropdownsContainer, 'drawer:', !!heatframeDrawer);
    return;
  }
  
  // For interactive mode, check drawer visibility; for background preload, skip this check
  if (!backgroundPreload) {
    const isDrawerVisible = heatframeDrawer.classList.contains('show') || 
                            window.getComputedStyle(heatframeDrawer).display !== 'none';
    
    if (!isDrawerVisible) {
      console.log('DEBUG: Heatframe drawer not visible, skipping initialization');
      return;
    }
  }
  
  console.log('DEBUG: All prerequisites met, creating/updating HeatframeFilter instance');
  
  // If we have a stale instance, clean it up first
  if (heatframeFilterInstance && !heatframeFilterInstance.initializationCompleted) {
    console.log('DEBUG: Cleaning up stale instance before creating new one');
    if (heatframeFilterInstance.cleanupCenteredOverlay) {
      heatframeFilterInstance.cleanupCenteredOverlay();
    }
  }
  
  // Create new instance if needed
  if (!heatframeFilterInstance || !heatframeFilterInstance.initializationCompleted) {
    heatframeFilterInstance = new HeatframeFilter();
    window.heatframeFilterInstance = heatframeFilterInstance; // For section reload compatibility
  } else {
    // Instance exists and is properly initialized - this shouldn't happen due to earlier checks
    console.log('DEBUG: Instance already exists and is valid');
  }
  
  } catch (error) {
    console.error('Task 141: Error during filter initialization:', error);
  } finally {
    // Task 141: Reset initialization lock
    isInitializing = false;
    console.log('DEBUG: Task 141 - Initialization lock released');
  }
}

// Background preloading function
async function preloadFilterSystem() {
  console.log('DEBUG: Starting background filter preload');
  
  // Check if container exists (should be available on page load)
  const dropdownsContainer = document.getElementById('dropdowns-container');
  if (!dropdownsContainer) {
    console.log('DEBUG: Dropdowns container not available for preload');
    return false;
  }
  
  // Check if heatframe cards are loaded
  const heatframeGrid = document.querySelector('.heatframe-product-grid');
  const hasCards = heatframeGrid && heatframeGrid.querySelectorAll('.config-card').length > 0;
  if (!hasCards) {
    console.log('DEBUG: Heatframe cards not loaded yet, skipping preload');
    return false;
  }
  
  console.log('DEBUG: Prerequisites met - preloading filter system in background');
  
  // Use the main initialization function in background mode
  await initializeHeatframeFilter(true); // true = background preload
  
  console.log('DEBUG: Background filter preload completed');
  return true;
}

// Simple event-driven initialization setup with background preloading
function setupFilterInitialization() {
  console.log('DEBUG: Setting up filter initialization with background preloading');
  
  // Background preload attempt when page is ready
  setTimeout(() => {
    preloadFilterSystem();
  }, 500);
  
  // Backup preload attempt after a longer delay
  setTimeout(() => {
    preloadFilterSystem();
  }, 2000);
  
  // Single click listener for heatframe drawer opening (now should be instant)
  document.addEventListener('click', (event) => {
    const configCard = event.target.closest('.config-card');
    if (configCard && configCard.getAttribute('data-content-type') === 'heatframe-selector') {
      console.log('DEBUG: Heatframe config card clicked - filters should already be preloaded');
      
      // Minimal delay since filters should be preloaded - interactive mode
      setTimeout(() => {
        initializeHeatframeFilter(false); // false = interactive mode
      }, 50);
    }
  });
  
  // Simple drawer visibility observer for reliable initialization
  const heatframeDrawer = document.getElementById('heatframe-selector');
  if (heatframeDrawer) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const hasShow = mutation.target.classList.contains('show');
          
          if (hasShow) {
            console.log('DEBUG: Drawer opened - using preloaded filters');
            // Even faster since filters should be preloaded - interactive mode
            setTimeout(() => initializeHeatframeFilter(false), 50);
          }
        }
      });
    });
    
    observer.observe(heatframeDrawer, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    console.log('DEBUG: Drawer visibility observer setup complete');
  }
}

// Export functions globally for section reload compatibility and testing
window.initializeHeatframeFilter = initializeHeatframeFilter;
window.preloadFilterSystem = preloadFilterSystem;

// Task 137: Global testing and debugging functions for centered overlay system
window.testCenteredOverlay = function() {
  console.log('DEBUG: Task 137 - Testing centered overlay system');
  
  if (!heatframeFilterInstance) {
    console.log('DEBUG: No heatframe filter instance found - initializing');
    initializeHeatframeFilter(false);
    return;
  }
  
  if (!heatframeFilterInstance.initializationCompleted) {
    console.log('DEBUG: Filter instance not completed - waiting for initialization');
    return;
  }
  
  // Find first available card dynamically using the same method as addCardClickListeners
  let availableCards = document.querySelectorAll('.heatframe-product-grid .config-card[data-config-card]');
  
  if (!availableCards || availableCards.length === 0) {
    // Try alternative selectors for dynamically loaded content
    availableCards = document.querySelectorAll('#secondSidebar .config-card[data-config-card], .heatframe-single-container .config-card[data-config-card]');
  }
  
  if (availableCards.length > 0) {
    console.log(`DEBUG: Found ${availableCards.length} cards, showing first card in centered overlay`);
    heatframeFilterInstance.showCenteredCard(availableCards[0]);
  } else {
    console.log('DEBUG: No cards available for testing - DOM might not be ready');
    console.log('DEBUG: Available config cards:', document.querySelectorAll('.config-card'));
  }
};

// Task 137: Debug function to check system status
window.debugCenteredOverlay = function() {
  console.log('DEBUG: Task 137 - Centered overlay system status:');
  
  if (!heatframeFilterInstance) {
    console.log('  - No filter instance');
    return;
  }
  
  console.log(`  - Filter initialized: ${heatframeFilterInstance.initializationCompleted}`);
  console.log(`  - Cards found: ${heatframeFilterInstance.cards ? heatframeFilterInstance.cards.length : 0}`);
  console.log(`  - Overlay container: ${heatframeFilterInstance.overlayContainer ? 'exists' : 'missing'}`);
  console.log(`  - Current centered card: ${heatframeFilterInstance.currentCenteredCard ? 'active' : 'none'}`);
  console.log(`  - Keyboard handler: ${heatframeFilterInstance.keyboardHandler ? 'active' : 'missing'}`);
  
  // Check DOM elements
  const overlayInDom = document.querySelector('.heatframe-centered-overlay');
  console.log(`  - Overlay in DOM: ${overlayInDom ? 'found' : 'missing'}`);
  
  const heatframeContainer = document.querySelector('.heatframe-single-container');
  console.log(`  - Heatframe container: ${heatframeContainer ? 'found' : 'missing'}`);
  
  const productGrid = document.querySelector('.heatframe-product-grid');
  console.log(`  - Product grid: ${productGrid ? 'found' : 'missing'}`);
  
  const configCards = document.querySelectorAll('.heatframe-product-grid .config-card');
  console.log(`  - Config cards in DOM: ${configCards.length}`);
};

// Task 138: Debug function for variant availability in centered overlays
window.debugCenteredOverlayValidation = function() {
  console.log('DEBUG: Task 138 - Centered overlay validation status:');
  
  if (!heatframeFilterInstance) {
    console.log('  - No filter instance');
    return;
  }
  
  console.log(`  - Availability instances tracked: ${heatframeFilterInstance.centeredCardAvailabilityInstances ? heatframeFilterInstance.centeredCardAvailabilityInstances.size : 0}`);
  
  if (heatframeFilterInstance.currentCenteredCard) {
    const clonedCard = heatframeFilterInstance.currentCenteredCard.cloned;
    console.log('  - Current centered card analysis:');
    
    // Check if variant JSON exists
    const variantJson = clonedCard.querySelector('[data-variants-json]');
    console.log(`    - Variant JSON: ${variantJson ? 'found' : 'missing'}`);
    
    // Check fieldsets
    const fieldsets = clonedCard.querySelectorAll('fieldset.js.config-pill');
    console.log(`    - Fieldsets: ${fieldsets.length}`);
    
    // Check pill buttons
    const pillButtons = clonedCard.querySelectorAll('input[type="radio"]');
    console.log(`    - Pill buttons: ${pillButtons.length}`);
    
    // Check disabled buttons
    const disabledButtons = clonedCard.querySelectorAll('input[type="radio"].disabled');
    console.log(`    - Disabled buttons: ${disabledButtons.length}`);
    
    // Check Add to Kit button
    const addToKitButton = clonedCard.querySelector('.log-variant-button');
    console.log(`    - Add to Kit button: ${addToKitButton ? 'found' : 'missing'}`);
    if (addToKitButton) {
      console.log(`      - Disabled: ${addToKitButton.classList.contains('disabled')}`);
    }
  } else {
    console.log('  - No centered card currently active');
  }
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('DEBUG: Heatframe filter script loaded - setting up background preloading and event-driven initialization');
  
  // Setup simplified initialization system with background preloading
  setupFilterInitialization();
  
  // Try immediate initialization if drawer is already open
  const heatframeDrawer = document.getElementById('heatframe-selector');
  if (heatframeDrawer && heatframeDrawer.classList.contains('show')) {
    console.log('DEBUG: Drawer already open on page load - initializing filters');
    setTimeout(() => initializeHeatframeFilter(false), 50); // Interactive mode, faster timing
  }
});